import type { Rating } from '@feedback-service/shared';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface RatingSelectProps {
  label: string;
  value?: Rating;
  onChange: (value: Rating | undefined) => void;
  disabled?: boolean;
  className?: string;
}

const RATING_OPTIONS: Array<{ value: Rating; emoji: string }> = [
  { value: 'angry', emoji: '😠' },
  { value: 'neutral', emoji: '😐' },
  { value: 'happy', emoji: '😊' },
];

/**
 * 满意度评分组件
 * 显示三个表情按钮，对应不同的评分等级
 */
export function RatingSelect({
  label,
  value,
  onChange,
  disabled = false,
  className,
}: RatingSelectProps) {
  const { t } = useTranslation();

  const handleSelect = (rating: Rating) => {
    if (value === rating) {
      onChange(undefined);
    } else {
      onChange(rating);
    }
  };

  return (
    <div className={`space-y-2 ${className || ''}`}>
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex gap-2" role="radiogroup" aria-label={label}>
        {RATING_OPTIONS.map((option) => (
          <Button
            key={option.value}
            type="button"
            variant={value === option.value ? 'default' : 'outline'}
            size="lg"
            className={cn(
              'text-2xl h-12 w-12 p-0',
              value === option.value && 'ring-2 ring-primary ring-offset-2'
            )}
            onClick={() => handleSelect(option.value)}
            disabled={disabled}
            role="radio"
            aria-checked={value === option.value}
            aria-label={t(`form.rating.${option.value}`)}
          >
            <span aria-hidden="true">{option.emoji}</span>
          </Button>
        ))}
      </div>
      {value && (
        <p className="text-xs text-muted-foreground">
          {t(`form.rating.${value}`)}
        </p>
      )}
    </div>
  );
}
